import { FC, MouseEvent, useState, useEffect, useRef } from 'react'

import styled from 'styled-components'

import { variables } from 'src/Styled/variables'

const TopBarWrapper = styled.div`
  grid-area: topbar;
  font-family: 'Segoe UI';
  line-height: 150%;
  font-style: normal;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1000;
  background: linear-gradient(135deg, #111111 10%, #2b2b2b 50%, #8b6e00 100%);
`

const InnerTopBarWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  column-gap: 20px;
  row-gap: 4px;
  align-items: center;
  padding: 5px 40px;
  font-size: ${variables.fontSize14};
  line-height: 1.4;
  position: relative;
`

const PromoMessage = styled.div`
  font-weight: 600;
  color: rgb(255, 215, 0);
  text-shadow: 0 0 5px rgba(255, 215, 0, 0.4);
  @media (max-width: 600px) {
    text-align: center;
  }
`

const ActionsWrapper = styled.div`
  width: 210px;
  background: rgba(255, 255, 255, 0.1);
  padding: 4px 12px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgb(255, 215, 0);
  font-weight: 600;
  border: 1px solid rgba(255, 215, 0, 0.5);
  @media (max-width: 600px) {
    justify-content: center;
    text-align: center;
  }
`

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 8px;
  right: 8px;
  color: #fff;

  &:hover {
    opacity: 0.8;
  }
`

export const StyledCopyButton = styled.button<{ $copied?: boolean }>`
  background: ${({ $copied }) => ($copied ? '#000' : 'rgb(255, 215, 0)')};
  color: ${({ $copied }) => ($copied ? '#fff' : '#000')};
  border: none;
  border-radius: 10px;
  font-size: 12px;
  padding: 2px 8px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background: ${({ $copied }) => ($copied ? '#111' : '#fff')};
    color: ${({ $copied }) => ($copied ? '#fff' : '#000')};
  }

  &:disabled {
    opacity: 0.8;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid rgba(255, 215, 0, 0.9);
    outline-offset: 2px;
  }
`
const TimerSection = styled.div`
  grid-area: timer;
  color: #ff5555;
  font-weight: 600;
  @media (max-width: 600px) {
    text-align: center;
  }
`

interface PromoTopBarProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isVisible: boolean
  value?: string
  setIsPromoVisible?: (isVisible: boolean) => void
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void
}

const RESET_TIMEOUT = 2000
const DEFAULT_PROMO_CODE = 'HLDY90'

export const PromoTopBar: FC<PromoTopBarProps> = (props) => {
  const {
    isVisible,
    value = DEFAULT_PROMO_CODE,
    onClick,
    setIsPromoVisible,
    ...restProps
  } = props

  const [copied, setCopied] = useState(false)
  const resetTimerRef = useRef<number | null>(null)

  useEffect(() => {
    const clear = (): void => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current)
        resetTimerRef.current = null
      }
    }

    clear()

    if (copied) {
      resetTimerRef.current = window.setTimeout(() => {
        setCopied(false)
        resetTimerRef.current = null
      }, RESET_TIMEOUT)
    }

    return clear
  }, [copied])

  const handleClose = (): void => {
    setIsPromoVisible && setIsPromoVisible(false)
  }

  const handleCopyPromocode = async (
    e: MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    await window.navigator.clipboard.writeText(value)
    onClick && onClick(e)
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current)
      resetTimerRef.current = null
    }
    setCopied(true)
  }

  if (!isVisible) {
    return null
  }

  return (
    <TopBarWrapper>
      <InnerTopBarWrapper>
        <PromoMessage>Holiday Season Sale - 17% OFF any campaign</PromoMessage>

        <ActionsWrapper>
          <div>Use Code: {value}</div>
          <StyledCopyButton
            {...restProps}
            $copied={copied}
            value={value}
            onClick={handleCopyPromocode}
            disabled={copied}
          >
            {copied ? 'Copied' : 'Copy'}
          </StyledCopyButton>
        </ActionsWrapper>

        <TimerSection>🎄 Hurry! Holiday offer ends soon!</TimerSection>
        <CloseButton
          type='button'
          aria-label='Close promotion bar'
          onClick={handleClose}
        >
          ✕
        </CloseButton>
      </InnerTopBarWrapper>
    </TopBarWrapper>
  )
}
